import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { AlertTriangle, Clock, DollarSign, Users, TrendingDown } from 'lucide-react';

export function RealityCheck() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const problems = [
    {
      icon: DollarSign,
      title: 'R$ 23.400 perdidos',
      description: 'Em decisões baseadas em dados incompletos',
      impact: 'Todo mês',
      color: '#FF6B35',
    },
    {
      icon: Clock,
      title: '67 horas desperdiçadas',
      description: 'Procurando dados que deveriam estar organizados',
      impact: 'Por mês/equipe',
      color: '#FFE66D',
    },
    {
      icon: Users,
      title: '43% dos visitantes',
      description: 'São bots, scrapers ou tráfego inválido',
      impact: 'Inflando seus custos',
      color: '#FF6B35',
    },
    {
      icon: TrendingDown,
      title: '89% de desperdício',
      description: 'Em campanhas sem rastreamento adequado',
      impact: 'Dinheiro no ralo',
      color: '#FFE66D',
    },
  ];

  return (
    <section ref={ref} className="relative py-24 bg-gradient-to-b from-[#0A0E1A] to-[#0B0E14] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #FF6B35 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FF6B35]/30 bg-[#FF6B35]/10 backdrop-blur-sm mb-6">
            <AlertTriangle className="w-4 h-4 text-[#FF6B35]" />
            <span className="text-[#FF6B35] font-mono text-sm uppercase tracking-wider">
              Realidade Brutal
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
            Enquanto Você Lê Isso,
            <br />
            <span className="bg-gradient-to-r from-[#FF6B35] to-[#FFE66D] bg-clip-text text-transparent">
              Está Perdendo Dinheiro
            </span>
          </h2>

          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Dados reais de empresas que operavam <span className="text-[#FF6B35]">sem visibilidade total</span> dos seus dados
          </p>
        </motion.div>

        {/* Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto mb-16">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-[#0B0E14]/90 to-[#0A0E1A]/90 backdrop-blur-lg hover:border-white/20 transition-all duration-300">
                {/* Icon */}
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                  style={{ 
                    background: `linear-gradient(135deg, ${problem.color}20 0%, ${problem.color}05 100%)`,
                    border: `1px solid ${problem.color}30`,
                  }}
                >
                  <problem.icon className="w-8 h-8" style={{ color: problem.color }} />
                </div>

                {/* Content */}
                <h3 className="text-3xl font-bold mb-3" style={{ color: problem.color }}>
                  {problem.title}
                </h3>
                <p className="text-gray-300 text-lg mb-4">
                  {problem.description}
                </p>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: problem.color }} />
                  <span className="text-sm text-gray-400 font-mono">{problem.impact}</span>
                </div>

                {/* Glow Effect */}
                <div 
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-2xl -z-10"
                  style={{ background: `radial-gradient(circle at center, ${problem.color}20 0%, transparent 70%)` }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Total Cost Calculator */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative p-8 rounded-3xl border-2 border-[#FF6B35]/30 bg-gradient-to-br from-[#FF6B35]/10 via-[#0B0E14] to-[#0B0E14] overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 opacity-20">
              <motion.div
                className="absolute inset-0"
                animate={{
                  background: [
                    'radial-gradient(circle at 0% 0%, #FF6B35 0%, transparent 50%)',
                    'radial-gradient(circle at 100% 100%, #FFE66D 0%, transparent 50%)',
                    'radial-gradient(circle at 0% 100%, #FF6B35 0%, transparent 50%)',
                    'radial-gradient(circle at 100% 0%, #FFE66D 0%, transparent 50%)',
                  ],
                }}
                transition={{ duration: 10, repeat: Infinity }}
              />
            </div>

            <div className="relative z-10 text-center">
              <p className="text-gray-400 text-lg mb-4">Custo Total Mensal da Desordem:</p>
              <motion.div
                initial={{ scale: 0.9 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="text-5xl md:text-7xl font-bold mb-6"
              >
                <span className="bg-gradient-to-r from-[#FF6B35] via-[#FFE66D] to-[#FF6B35] bg-clip-text text-transparent">
                  R$ 47.200
                </span>
              </motion.div>
              <p className="text-gray-300 text-lg mb-8">
                Média calculada com base em <span className="text-[#FFE66D]">1.247 empresas</span> antes de implementar o WPTT
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] rounded-xl font-semibold text-white shadow-2xl"
                >
                  Parar de Perder Agora
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="px-8 py-4 border-2 border-[#FFE66D]/50 rounded-xl font-semibold text-[#FFE66D] hover:bg-[#FFE66D]/10 transition-all"
                >
                  Ver Como Recuperar
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
